import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Flask, Plus, MagnifyingGlass, Funnel, Copy, ArrowsMerge, Trash, CaretLeft, CaretRight } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import MergeStudiesDialog from '../components/MergeStudiesDialog';
import ESignature from '../components/ESignature';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useStatuses, statusLabel } from '../hooks/useStatuses';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

const statusColors = {
  activo: 'bg-green-50 text-green-700 border-green-200',
  finalizado: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelado: 'bg-stone-50 text-stone-500 border-stone-300',
};

const productTypeColors = {
  cosmetico: 'bg-pink-50 text-pink-700 border-pink-200',
  cosmetico_plus: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  sanitario: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  suplemento: 'bg-orange-50 text-orange-700 border-orange-200',
  medicamento: 'bg-purple-50 text-purple-700 border-purple-200',
};

export default function Studies() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  // URS-F486: estados configurables por el admin
  const { statuses: studyStatuses, byKey: studyStatusByKey } = useStatuses('study');
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const canEdit = can('studies', 'edit');
  const canDelete = can('studies', 'delete');
  const [studies, setStudies] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('activo');
  const [filterProduct, setFilterProduct] = useState('all');
  // URS-F460: nuevos filtros — subtipo, producto y rango de fechas
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterProductName, setFilterProductName] = useState('');
  const [debouncedProductName, setDebouncedProductName] = useState('');
  const [filterStartFrom, setFilterStartFrom] = useState('');
  const [filterStartTo, setFilterStartTo] = useState('');
  const [filterEndFrom, setFilterEndFrom] = useState('');
  const [filterEndTo, setFilterEndTo] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [filterDup, setFilterDup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // pagination state (server-side)
  const [page, setPage] = useState(1);
  const [totalStudies, setTotalStudies] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const labels = lang === 'es'
    ? { title: 'Estudios', new: 'Nuevo Estudio', search: 'Buscar por codigo, titulo o cliente...', no_studies: 'No hay estudios registrados', code: 'Codigo', study_title: 'Titulo', client: 'Cliente', product: 'Producto', phase: 'Subtipo', status: 'Estado', dates: 'Fechas', all: 'Todos', activo: 'Activo', finalizado: 'Finalizado', cancelado: 'Cancelado', cosmetico: 'Cosmetico', cosmetico_plus: 'Cosmetico Plus', sanitario: 'Sanitario', suplemento: 'Suplemento', medicamento: 'Medicamento', filter_status: 'Estado', filter_product: 'Tipo producto', page_of: 'de', prev: 'Anterior', next: 'Siguiente', showing: 'Mostrando', of: 'de' }
    : { title: 'Studies', new: 'New Study', search: 'Search by code, title or client...', no_studies: 'No studies registered', code: 'Code', study_title: 'Title', client: 'Client', product: 'Product', phase: 'Subtype', status: 'Status', dates: 'Dates', all: 'All', activo: 'Active', finalizado: 'Completed', cancelado: 'Cancelled', cosmetico: 'Cosmetic', cosmetico_plus: 'Cosmetic Plus', sanitario: 'Medical Device', suplemento: 'Supplement', medicamento: 'Drug', filter_status: 'Status', filter_product: 'Product type', page_of: 'of', prev: 'Previous', next: 'Next', showing: 'Showing', of: 'of' };

  // Debounce search to avoid hammering the API on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 350); return () => clearTimeout(t); }, [search]);
  // URS-F460: debounce del filtro "producto" (busqueda por texto contains)
  useEffect(() => { const t = setTimeout(() => setDebouncedProductName(filterProductName), 350); return () => clearTimeout(t); }, [filterProductName]);

  // Reset to page 1 whenever any filter changes.
  useEffect(() => { setPage(1); }, [debouncedSearch, filterStatus, filterProduct, filterPhase, debouncedProductName, filterStartFrom, filterStartTo, filterEndFrom, filterEndTo, filterDup]);

  const loadStudies = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterDup) {
      params.set('duplicates', 'true');
    } else {
      params.set('paginated', 'true');
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterProduct !== 'all') params.set('product_type', filterProduct);
      // URS-F460: nuevos filtros
      if (filterPhase !== 'all') params.set('phase', filterPhase);
      if (debouncedProductName) params.set('product', debouncedProductName);
      if (filterStartFrom) params.set('start_date_from', filterStartFrom);
      if (filterStartTo) params.set('start_date_to', filterStartTo);
      if (filterEndFrom) params.set('end_date_from', filterEndFrom);
      if (filterEndTo) params.set('end_date_to', filterEndTo);
      if (debouncedSearch) params.set('q', debouncedSearch);
    }
    Promise.all([
      axios.get(`${API_URL}/api/studies?${params.toString()}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/clients?all=true`, { withCredentials: true }),
    ]).then(([sr, cr]) => {
      if (Array.isArray(sr.data)) {
        // duplicates view returns flat array (legacy).
        setStudies(sr.data);
        setTotalStudies(sr.data.length);
        setTotalPages(1);
      } else {
        setStudies(sr.data.data || []);
        setTotalStudies(sr.data.total || 0);
        setTotalPages(sr.data.pages || 0);
      }
      setClients(Array.isArray(cr.data) ? cr.data : cr.data.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [page, filterStatus, filterProduct, filterPhase, debouncedProductName, filterStartFrom, filterStartTo, filterEndFrom, filterEndTo, filterDup, debouncedSearch]);

  useEffect(() => { loadStudies(); }, [loadStudies]);

  const handleDelete = async (studyId, signature) => {
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}`, { withCredentials: true, data: { signature } });
      toast.success(lang === 'es' ? 'Estudio eliminado' : 'Study deleted');
      setDeleteTarget(null);
      loadStudies();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const clientMap = {};
  clients.forEach(c => { clientMap[c.client_id] = c.name; });

  // No client-side filtering needed when server returns the slice we want.
  // The only exception is the legacy duplicates view, which still expects
  // local-side filtering by search & client.
  const filtered = filterDup
    ? studies.filter(s => {
        const matchSearch = !debouncedSearch ||
          (s.code || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          (s.title || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          (s.study_id || '').toLowerCase().includes(debouncedSearch.toLowerCase());
        return matchSearch;
      })
    : studies;

  return (
    <div className="space-y-6" data-testid="studies-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Flask size={28} weight="duotone" className="text-[#1f3e80]" />
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{labels.title}</h1>
          <Badge variant="outline" className="text-xs font-mono">{totalStudies}</Badge>
        </div>
        <Button data-testid="create-study-btn"
          onClick={() => navigate('/studies/new')}
          disabled={!canEdit}
          title={!canEdit ? (lang === 'es' ? 'Sin permiso de edicion' : 'No edit permission') : ''}
          className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2 disabled:bg-stone-300 disabled:hover:bg-stone-300">
          <Plus size={16} />
          {labels.new}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="study-search" placeholder={labels.search} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-stone-200" />
        </div>
        <div className="flex items-center gap-2">
          <Funnel size={16} className="text-stone-400" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="study-filter-status" className="w-36 bg-white border-stone-200 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{labels.all}</SelectItem>
              {studyStatuses.length > 0 ? (
                studyStatuses.map(s => (
                  <SelectItem key={s.key} value={s.key}>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                      {lang === 'es' ? s.label_es : s.label_en}
                    </span>
                  </SelectItem>
                ))
              ) : (
                <>
                  <SelectItem value="activo">{labels.activo}</SelectItem>
                  <SelectItem value="finalizado">{labels.finalizado}</SelectItem>
                  <SelectItem value="cancelado">{labels.cancelado}</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          <Select value={filterProduct} onValueChange={setFilterProduct}>
            <SelectTrigger data-testid="study-filter-product" className="w-36 bg-white border-stone-200 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{labels.all}</SelectItem>
              <SelectItem value="cosmetico">{labels.cosmetico}</SelectItem>
              <SelectItem value="cosmetico_plus">{labels.cosmetico_plus}</SelectItem>
              <SelectItem value="sanitario">{labels.sanitario}</SelectItem>
              <SelectItem value="suplemento">{labels.suplemento}</SelectItem>
              <SelectItem value="medicamento">{labels.medicamento}</SelectItem>
            </SelectContent>
          </Select>
          {/* URS-F460: filtro subtipo */}
          <Select value={filterPhase} onValueChange={setFilterPhase}>
            <SelectTrigger data-testid="study-filter-phase" className="w-36 bg-white border-stone-200 text-sm">
              <SelectValue placeholder={lang === 'es' ? 'Subtipo' : 'Subtype'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'es' ? 'Todos subtipos' : 'All subtypes'}</SelectItem>
              {['Capilar', 'Corporal', 'Dental', 'Facial', 'Ocular',
                'Ginecologia', 'Med. Estetica', 'Odontologia', 'Oftalmologo', 'Otorrino', 'Tricologia', 'Urologia',
              ].map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMoreFilters(p => !p)}
            data-testid="study-filter-toggle-advanced"
            className="gap-1.5 text-xs text-stone-600"
          >
            <Funnel size={14} /> {lang === 'es' ? (showMoreFilters ? 'Menos' : 'Mas filtros') : (showMoreFilters ? 'Less' : 'More filters')}
          </Button>
          <Button
            variant={filterDup ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterDup(p => !p)}
            data-testid="study-filter-duplicates"
            className={`gap-1.5 text-xs ${filterDup ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'text-stone-600'}`}
          >
            <Copy size={14} /> {lang === 'es' ? 'Duplicados' : 'Duplicates'}
          </Button>
        </div>
      </div>

      {/* URS-F460: filtros avanzados (producto + rangos de fechas) */}
      {showMoreFilters && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-stone-50 border border-stone-200 rounded-sm p-3">
          <div className="space-y-1 md:col-span-1">
            <label className="text-[11px] uppercase font-semibold text-stone-500 tracking-wider">
              {lang === 'es' ? 'Producto (nombre)' : 'Product (name)'}
            </label>
            <Input
              data-testid="study-filter-product-name"
              placeholder={lang === 'es' ? 'Contiene...' : 'Contains...'}
              value={filterProductName}
              onChange={e => setFilterProductName(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] uppercase font-semibold text-stone-500 tracking-wider">
              {lang === 'es' ? 'Inicio desde' : 'Start from'}
            </label>
            <Input
              data-testid="study-filter-start-from"
              type="date"
              value={filterStartFrom}
              onChange={e => setFilterStartFrom(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] uppercase font-semibold text-stone-500 tracking-wider">
              {lang === 'es' ? 'Inicio hasta' : 'Start to'}
            </label>
            <Input
              data-testid="study-filter-start-to"
              type="date"
              value={filterStartTo}
              onChange={e => setFilterStartTo(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] uppercase font-semibold text-stone-500 tracking-wider">
              {lang === 'es' ? 'Fin desde' : 'End from'}
            </label>
            <Input
              data-testid="study-filter-end-from"
              type="date"
              value={filterEndFrom}
              onChange={e => setFilterEndFrom(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] uppercase font-semibold text-stone-500 tracking-wider">
              {lang === 'es' ? 'Fin hasta' : 'End to'}
            </label>
            <Input
              data-testid="study-filter-end-to"
              type="date"
              value={filterEndTo}
              onChange={e => setFilterEndTo(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
          {(filterProductName || filterStartFrom || filterStartTo || filterEndFrom || filterEndTo || filterPhase !== 'all') && (
            <div className="md:col-span-5 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                data-testid="study-filter-clear-advanced"
                onClick={() => {
                  setFilterProductName('');
                  setFilterStartFrom('');
                  setFilterStartTo('');
                  setFilterEndFrom('');
                  setFilterEndTo('');
                  setFilterPhase('all');
                }}
                className="text-xs text-stone-500 hover:text-stone-700"
              >
                {lang === 'es' ? 'Limpiar filtros' : 'Clear filters'}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><p className="text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Flask size={40} weight="duotone" className="text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">{labels.no_studies}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">ID</TableHead>
                <TableHead className="w-36">{labels.code}</TableHead>
                <TableHead>{labels.study_title}</TableHead>
                <TableHead>{labels.client}</TableHead>
                <TableHead>{labels.product}</TableHead>
                <TableHead>{labels.phase}</TableHead>
                <TableHead>{labels.status}</TableHead>
                <TableHead>{labels.dates}</TableHead>
                <TableHead className="w-28"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s, idx) => {
                const prevStudy = idx > 0 ? filtered[idx - 1] : null;
                const nextStudy = idx < filtered.length - 1 ? filtered[idx + 1] : null;
                const sameGroupPrev = filterDup && prevStudy && s.duplicate_group && prevStudy.duplicate_group === s.duplicate_group;
                const sameGroupNext = filterDup && nextStudy && s.duplicate_group && nextStudy.duplicate_group === s.duplicate_group;
                const isFirstOfPair = filterDup && !sameGroupPrev && sameGroupNext;
                return (
                <TableRow key={s.study_id} data-testid={`study-row-${s.study_id}`} className={`hover:bg-stone-50 cursor-pointer ${sameGroupPrev ? 'border-t-0 bg-amber-50/40' : ''}`} onClick={() => navigate(`/studies/${s.study_id}`)}>
                  <TableCell className="font-mono text-xs text-stone-500">{s.study_id}</TableCell>
                  <TableCell className="font-mono text-sm font-medium text-stone-800">{s.code}</TableCell>
                  <TableCell className="text-sm font-medium text-stone-900 max-w-[250px] truncate">{s.title}</TableCell>
                  <TableCell className="text-sm text-stone-600">{clientMap[s.client_id] || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${productTypeColors[s.product_type] || ''}`}>
                      {labels[s.product_type] || s.product_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-stone-600">{s.phase || '-'}</TableCell>
                  <TableCell>
                    {(() => {
                      const cfg = studyStatusByKey[s.status];
                      if (cfg) {
                        return (
                          <Badge
                            variant="outline"
                            className="text-xs border"
                            style={{ backgroundColor: `${cfg.color}18`, color: cfg.color, borderColor: `${cfg.color}55` }}
                            data-testid={`study-status-badge-${s.study_id}`}
                          >
                            {statusLabel(studyStatusByKey, s.status, lang)}
                          </Badge>
                        );
                      }
                      return (
                        <Badge variant="outline" className={`text-xs ${statusColors[s.status] || ''}`}>
                          {labels[s.status] || s.status}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-xs text-stone-500 whitespace-nowrap">
                    {s.start_date ? s.start_date : '-'}
                    {s.end_date ? ` / ${s.end_date}` : ''}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {isFirstOfPair && isAdmin && (
                        <Button variant="outline" size="sm" data-testid={`merge-study-${s.study_id}`}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMergeTarget({ a: s, b: nextStudy }); }}
                          className="h-7 px-2.5 text-[11px] text-emerald-600 hover:text-emerald-700 border-emerald-300 hover:bg-emerald-50 gap-1">
                          <ArrowsMerge size={13} /> {lang === 'es' ? 'Fusionar' : 'Merge'}
                        </Button>
                      )}
                      {(isAdmin || canDelete) && (
                        <Button variant="ghost" size="icon" data-testid={`delete-study-${s.study_id}`}
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                          className="h-7 w-7 text-stone-400 hover:text-red-600">
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination controls (hidden in duplicates view) */}
      {!filterDup && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-stone-600" data-testid="studies-pagination">
          <div>
            {labels.showing} <span className="font-medium text-stone-800">{(page - 1) * PAGE_SIZE + 1}</span>-
            <span className="font-medium text-stone-800">{Math.min(page * PAGE_SIZE, totalStudies)}</span> {labels.of}{' '}
            <span className="font-medium text-stone-800">{totalStudies}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="studies-page-prev" variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))} className="h-8 gap-1 text-xs">
              <CaretLeft size={14} /> {labels.prev}
            </Button>
            {(() => {
              // Compact page list: 1 ... p-1 p p+1 ... last
              const pagesToShow = new Set([1, totalPages, page - 1, page, page + 1]);
              const ordered = Array.from(pagesToShow).filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
              const nodes = [];
              ordered.forEach((p, i) => {
                if (i > 0 && p - ordered[i - 1] > 1) {
                  nodes.push(<span key={`gap-${p}`} className="text-stone-400 px-1">...</span>);
                }
                nodes.push(
                  <Button key={p} data-testid={`studies-page-${p}`} variant={p === page ? 'default' : 'outline'} size="sm"
                    onClick={() => setPage(p)}
                    className={`h-8 w-8 p-0 text-xs ${p === page ? 'bg-[#1f3e80] hover:bg-[#2a4f8a] text-white' : 'text-stone-600'}`}>
                    {p}
                  </Button>
                );
              });
              return nodes;
            })()}
            <Button data-testid="studies-page-next" variant="outline" size="sm" disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="h-8 gap-1 text-xs">
              {labels.next} <CaretRight size={14} />
            </Button>
          </div>
        </div>
      )}

      <ESignature
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSigned={(signature) => handleDelete(deleteTarget.study_id, signature)}
        action={lang === 'es' ? `Eliminar estudio ${deleteTarget?.study_id || ''}` : `Delete study ${deleteTarget?.study_id || ''}`}
        resource={`study/${deleteTarget?.study_id || ''}`}
        lang={lang}
      />

      <MergeStudiesDialog
        open={!!mergeTarget}
        onClose={() => setMergeTarget(null)}
        studyA={mergeTarget?.a}
        studyB={mergeTarget?.b}
        lang={lang}
        onMerged={() => { setMergeTarget(null); loadStudies(); }}
      />
    </div>
  );
}
